import React, { useState, useEffect } from 'react';
import metricsService from '@/services/metricsService';
import MetricCard from '@/components/admin/metrics/MetricCard';
import AlertsPanel from '@/components/admin/metrics/AlertsPanel';
import '@/styles/MetricsDashboard.css';
import '@/styles/MetricCard.css';
import '@/styles/AlertsPanel.css';
import { getMetricsConfig } from '@/config/metrics';

const MetricsDashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('24h');

  const { refreshInterval } = getMetricsConfig();

  const loadDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [dashboard, alerts] = await Promise.all([
        metricsService.getDashboard(timeRange),
        metricsService.getAlerts(),
      ]);
      
      setDashboardData({
        ...dashboard.data,
        alerts: alerts.data,
      });
      setError(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Dashboard loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, refreshInterval);
    return () => clearInterval(interval);
  }, [timeRange, refreshInterval, loadDashboardData]);

  if (loading && !dashboardData) {
    return (
      <div className="metrics-dashboard loading">
        <div className="loading-spinner">Loading metrics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metrics-dashboard error">
        <div className="error-message">
          <h3>⚠️ Dashboard Error</h3>
          <p>{error}</p>
          <button onClick={loadDashboardData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="metrics-dashboard">
      <header className="dashboard-header">
        <h1>📊 Saunadip Metrics Dashboard</h1>
        <div className="dashboard-controls">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button onClick={loadDashboardData} className="refresh-btn">
            🔄 Refresh
          </button>
        </div>
      </header>

      <div className="metrics-grid">
        <MetricCard
          title="🟢 System Health"
          data={dashboardData?.system_health}
          type="system"
        />
        <MetricCard
          title="⚡ Performance"
          data={dashboardData?.application_performance}
          type="performance"
        />
        <MetricCard
          title="💰 Business"
          data={dashboardData?.business_metrics}
          type="business"
        />
        <MetricCard
          title="👥 User Experience"
          data={dashboardData?.user_experience}
          type="ux"
        />
        <MetricCard
          title="🔒 Security"
          data={dashboardData?.security_compliance}
          type="security"
        />
        <MetricCard
          title="⚙️ Operations"
          data={dashboardData?.operational_metrics}
          type="operational"
        />
      </div>

      <AlertsPanel alerts={dashboardData?.alerts} />
    </div>
  );
};

export default MetricsDashboard;
